import type { MouseEvent } from "react";
import { THECORE_LEARN_URL } from "@/platform/external-links";
import { isPlainNavClick } from "@/platform/lib/nav";
import thecoreMark from "@/render/assets/thecore-mark.png";

// An absolute http(s) target means the learning content lives off-site; a site-relative path means
// it is still our own in-app /thecore Compendium. The chip renders differently for each.
const isExternal = /^https?:\/\//i.test(THECORE_LEARN_URL);

// The "Learn TheCore" referral: the old header TheCore lockup (the reticle mark plus a two-tone
// wordmark) plus a trailing arrow, sitting beside the Profile picker when a TheCore Preset is the
// active source. Rendered as an inline link (the control bar's bordered elements are all
// selectors/toggles, so a border here would read as "pick this"); it materialises a button on hover.
// It adapts to where TheCore's learning content lives (see external-links.ts):
//   in-app  - a real <a href="/thecore"> enhanced to client-side navigation, like the old header link;
//   off-site - a real <a target="_blank"> with no client-nav. The referrer is kept (rel omits
//             noreferrer) so the destination can attribute Keylens traffic.
// A trailing up-right "off-site" arrow is always shown to flag that this leads to TheCore's learning
// content elsewhere; the destination is the in-app /thecore today and becomes truly external on the
// THECORE_LEARN_URL swap, so the arrow is the final off-site glyph from the start, by intent. The
// mark is decorative; the visible "The"+"Core" carry the name, and an aria-label states the off-site
// behaviour for assistive tech when external.
export function LearnTheCore({ onNavigate }: { onNavigate?: (to: string) => void }) {
  const go = (e: MouseEvent) => {
    if (!isExternal && onNavigate && isPlainNavClick(e)) {
      e.preventDefault();
      onNavigate(THECORE_LEARN_URL);
    }
  };
  return (
    <a
      className="learn-tc"
      href={THECORE_LEARN_URL}
      onClick={go}
      {...(isExternal ? { target: "_blank", rel: "noopener" } : {})}
      aria-label={
        isExternal ? "Learn TheCore (opens the TheCore site in a new tab)" : "Learn TheCore"
      }
      title={isExternal ? "Learn TheCore. Opens the TheCore site in a new tab." : "Learn TheCore"}
    >
      <img className="tc-mark" src={thecoreMark} alt="" aria-hidden="true" width={18} height={18} />
      <span className="learn-tc-verb">Learn</span>
      <span className="tc-word">
        <span className="tc-the">The</span>
        <span className="tc-core">Core</span>
      </span>
      <svg className="learn-tc-ext" viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M9 7h8v8M17 7 7 17"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
}
